import {
  type RouteLocationNormalized,
  type RouteLocationRaw,
} from 'vue-router';
import { useAuthStore, PATH_DICTIONARY } from '~/domains/auth';
import { usePermissions } from '~/infrastructure';
import { getUserInfoViewModel, useUserStore } from '~/domains/user';
import { checkNeedToChangePassword, setExternalDomainReferrer } from '../utils';
import { storeToRefs } from 'pinia';
import { useProductForceResetTable } from '~/domains/product';
import { loginFromAppGuard } from './loginFromAppGuard.ts';
import { sessionCheckGuard } from './sessionCheckGuard';

const authGuard = async (
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | boolean | undefined> => {
  const authStore = useAuthStore();
  const { user } = storeToRefs(useUserStore());
  const { checkAccess, checkNotAuthAccess } = usePermissions();
  const { fetchUserInfo } = getUserInfoViewModel();
  const { handleNeedResetTable } = useProductForceResetTable();
  setExternalDomainReferrer();

  if (authStore.isAuth) {
    // Check session of auth
    const sessionRedirect = await sessionCheckGuard(to);
    if (sessionRedirect !== undefined) return sessionRedirect;

    // Check login from app
    const isLoginFromApp = await loginFromAppGuard(to);
    if (isLoginFromApp) {
      return;
    }

    if (user.value === null) await fetchUserInfo();

    handleNeedResetTable();
    const needToChangePassword = checkNeedToChangePassword(to);
    if (needToChangePassword !== undefined) return needToChangePassword;

    if (!checkAccess(to)) {
      return PATH_DICTIONARY.home;
    }

    return to.meta.layout === 'auth' ? PATH_DICTIONARY.home : true;
  }

  if (!checkNotAuthAccess(to)) {
    return PATH_DICTIONARY.login;
  }

  const langQuery = to.query.lang as string | undefined;

  if (to.path === PATH_DICTIONARY.changePassword) {
    return PATH_DICTIONARY.login;
  }

  if (to.path === PATH_DICTIONARY.login && typeof langQuery === 'string') {
    const onlyQueryString = to.fullPath.replace(/^[^?]*/, '');
    authStore.setRedirectAfterLogin(`/${onlyQueryString}`);
  }

  if (to.meta.layout === 'auth') return true;

  authStore.setRedirectAfterLogin(to.fullPath);

  return typeof langQuery === 'string'
    ? `${PATH_DICTIONARY.login}?/lang=${langQuery}`
    : PATH_DICTIONARY.login;
};

export default authGuard;
